import React, { useState } from 'react';
import { Modal, Button, Form, Alert, ButtonGroup } from 'react-bootstrap';
import { CheckCircle, AlertTriangle } from 'lucide-react';

interface QAReviewModalProps {
  show: boolean;
  onHide: () => void;
  stepTitle: string;
  onSubmit: (qaStatus: 'pass' | 'fail', message?: string) => Promise<void>;
  existingQAResult?:
    | {
        qaStatus: 'pass' | 'fail';
        message?: string;
      }
    | undefined;
}

export const QAReviewModal: React.FC<QAReviewModalProps> = ({
  show,
  onHide,
  stepTitle,
  onSubmit,
  existingQAResult,
}) => {
  const [qaStatus, setQaStatus] = useState<'pass' | 'fail'>(existingQAResult?.qaStatus || 'pass');
  const [message, setMessage] = useState(existingQAResult?.message || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      await onSubmit(qaStatus, message || undefined);
      onHide();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit QA review');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>QA Review: {stepTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Form>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">QA Status *</Form.Label>
            <div className="d-flex gap-2">
              <ButtonGroup className="w-100">
                <Button
                  variant={qaStatus === 'pass' ? 'success' : 'outline-success'}
                  onClick={() => setQaStatus('pass')}
                  className="d-flex align-items-center justify-content-center gap-2"
                >
                  <CheckCircle size={16} />
                  Pass
                </Button>
                <Button
                  variant={qaStatus === 'fail' ? 'danger' : 'outline-danger'}
                  onClick={() => setQaStatus('fail')}
                  className="d-flex align-items-center justify-content-center gap-2"
                >
                  <AlertTriangle size={16} />
                  Fail
                </Button>
              </ButtonGroup>
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Message (Optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add any notes or comments..."
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Submitting...' : 'Submit QA Review'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
